import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { Editor, NgxEditorModule, Toolbar } from 'ngx-editor';
import { AlertCardComponent } from '../../../../shared/components/alert-card/alert-card.component';
import { FooterActionComponent } from '../../../../shared/components/footer-action/footer-action.component';
import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { LoadingBoxComponent } from '@shared/components/loading-box/loading-box.component';
import { TipoCampoEnum } from '@shared/enums/compras/tipo-campo.enum';
import { TipoFormatoEnvioEnum } from '@shared/enums/compras/tipo-formato-envio.enum';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { ConfiguracaoCotacaoService } from '../../../../shared/services/apis/compras/configuracao-cotacao.service';
import { ConfiguracaoCotacaoRequest } from '../../../../shared/services/requests/compras/configuracao-cotacao.request';
import { GetConfiguracaoCotacaoResponse } from '../../../../shared/services/responses/compras/configuracao-cotacao.response';
import { AuthService } from '../../../../shared/services/apis/usuario/auth.service';
import { GetEmpresaService } from '../../../../shared/services/apis/usuario/get-empresa.service';
import { EmpresaStruct } from '../../../../shared/services/structs/usuario/empresa.struct';
import { PortalCotacaoPreviewComponent } from '../comunicacao-preview/portal-cotacao-preview.component';
import { PortalPreviewData } from '../comunicacao-preview/portal-preview.model';

interface PresetColor {
  name: string;
  color: string;
  heroBg: string;
  heroBgEnd: string;
}

interface FormatOption {
  key: string;
  id: TipoFormatoEnvioEnum;
  label: string;
  icon: string;
  hint: string;
}

interface FieldConfig {
  key: string;
  idTipoCampo: TipoCampoEnum;
  name: string;
  type: string;
  icon: string;
  required: boolean;
  hint?: string;
}

interface AutoField {
  name: string;
  meta: string;
  icon: string;
}

interface TemplateVariable {
  codigo: string;
  descricao: string;
}

@Component({
  selector: 'app-comunicacao-cotacao',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSlideToggleModule,
    MatTooltipModule,
    NgxEditorModule,
    AlertCardComponent,
    HeaderComponent,
    FooterActionComponent,
    LoadingBoxComponent,
  ],
  templateUrl: './comunicacao-cotacao.component.html',
  styleUrls: ['./comunicacao-cotacao.component.css'],
})
export class ComunicacaoCotacaoComponent implements OnInit, OnDestroy {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Suprimentos;
  public form: FormGroup;
  public isSaving = false;
  public isLoading = false;
  /** Só com a configuração do servidor aplicada o salvar fica liberado: senão gravaria a tela vazia. */
  public configCarregada = false;
  public logoDataUrl: string | null = null;
  public editorEmail = new Editor();
  public showVariaveisEmail = false;
  public emailAssuntoPadrao = '';
  public emailCorpoPadrao = '';

  public toolbar: Toolbar = [
    ['bold', 'italic'],
    ['underline', 'strike'],
    ['ordered_list', 'bullet_list'],
    [{ heading: ['h1', 'h2', 'h3'] }],
    ['text_color', 'background_color'],
    ['align_left', 'align_center', 'align_right', 'align_justify'],
  ];

  public readonly accent = '#7A4FBF';

  public readonly presetColors: PresetColor[] = [
    { name: 'Vermelho ATS', color: '#B73232', heroBg: '#1C1B1F', heroBgEnd: '#2D2A3A' },
    { name: 'Azul corporativo', color: '#1E5DB8', heroBg: '#0D2A4F', heroBgEnd: '#1A3D6E' },
    { name: 'Verde corporativo', color: '#1E8E3E', heroBg: '#0F2D1A', heroBgEnd: '#1C4A2A' },
    { name: 'Roxo', color: '#7A4FBF', heroBg: '#1E1233', heroBgEnd: '#322050' },
    { name: 'Laranja', color: '#C26A00', heroBg: '#2D1B05', heroBgEnd: '#4A2D0E' },
    { name: 'Cinza neutro', color: '#374151', heroBg: '#111827', heroBgEnd: '#1F2937' },
  ];

  public readonly formatOptions: FormatOption[] = [
    { key: 'formatoLinkPortal', id: TipoFormatoEnvioEnum.LinkPortal, label: 'Link para o portal', icon: 'link', hint: 'Link único enviado por email - o fornecedor preenche o formulário online, sem necessidade de login.' },
    { key: 'formatoPlanilhaExcel', id: TipoFormatoEnvioEnum.PlanilhaExcel, label: 'Planilha (Excel)', icon: 'table_chart', hint: 'Planilha .xlsx para o fornecedor preencher e devolver por email - ideal para quem não usa o portal.' },
    { key: 'formatoPdfPreencher', id: TipoFormatoEnvioEnum.PdfPreencher, label: 'PDF para preencher', icon: 'picture_as_pdf', hint: 'PDF com os campos da cotação enviado como anexo - o fornecedor responde por email.' },
  ];

  public readonly autoFields: AutoField[] = [
    { name: 'Produtos solicitados', meta: 'nome, SKU, quantidade, unidade', icon: 'inventory_2' },
    { name: 'Data de entrega desejada', meta: 'campo de data', icon: 'event' },
    { name: 'Condições de pagamento aceitas', meta: 'lista pré-aprovada pela empresa', icon: 'payments' },
  ];

  public readonly fields: FieldConfig[] = [
    { key: 'campoPrecoUnitario', idTipoCampo: TipoCampoEnum.Moeda, name: 'Preço unitário por produto', type: 'Moeda', icon: 'attach_money', required: true },
    { key: 'campoIpi', idTipoCampo: TipoCampoEnum.Moeda, name: 'IPI estimado por produto', type: 'Moeda', icon: 'attach_money', required: false },
    { key: 'campoSt', idTipoCampo: TipoCampoEnum.Moeda, name: 'ST estimado por produto', type: 'Moeda', icon: 'attach_money', required: false },
    { key: 'campoPrazoEntrega', idTipoCampo: TipoCampoEnum.Numero, name: 'Prazo de entrega', type: 'Número', icon: '123', required: true },
    // Catálogo tipo_campo 9 (SimNao) a confirmar no banco antes de trocar (dossiê UD-08)
    { key: 'campoDisponibilidade', idTipoCampo: TipoCampoEnum.Selecao, name: 'Disponibilidade', type: 'Sim/Não', icon: 'toggle_on', required: true, hint: 'Sim/Não + qtd disponível' },
    { key: 'campoFrete', idTipoCampo: TipoCampoEnum.Moeda, name: 'Frete', type: 'Moeda', icon: 'local_shipping', required: false, hint: 'Valor + modalidade' },
    { key: 'campoCondicaoPagamento', idTipoCampo: TipoCampoEnum.Texto, name: 'Condição de pagamento ofertada', type: 'Texto', icon: 'short_text', required: true },
    { key: 'campoValidadeProposta', idTipoCampo: TipoCampoEnum.Data, name: 'Validade da proposta', type: 'Data', icon: 'event', required: true },
  ];

  public readonly variaveisEmail: TemplateVariable[] = [
    { codigo: '{numero_cotacao}', descricao: 'Número da cotação' },
    { codigo: '{empresa}', descricao: 'Nome da empresa compradora' },
    { codigo: '{nome_fornecedor}', descricao: 'Nome do fornecedor convidado' },
    { codigo: '{lista_produtos}', descricao: 'Lista dos produtos solicitados' },
    { codigo: '{link_formulario}', descricao: 'Link do formulário online da cotação' },
  ];

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private alertService: AlertService,
    private configuracaoCotacaoService: ConfiguracaoCotacaoService,
    private authService: AuthService,
    private getEmpresaService: GetEmpresaService,
    private dialog: MatDialog
  ) {
    this.form = this.createForm();
  }

  // Abre o preview do formulário como o fornecedor verá, usando o estado ATUAL
  // do formulário (inclusive alterações ainda não salvas).
  public abrirPreview(): void {
    const v = this.form.getRawValue();
    const data: PortalPreviewData = {
      marcaSigla: v.brandShort || 'ATS',
      marcaRodape: v.brandTagline || '',
      urlLogo: this.logoDataUrl,
      corMarca: v.brandColor || '#B73232',
      corHeroInicio: v.heroBg || '#1C1B1F',
      corHeroFim: v.heroBgEnd || '#2D2A3A',
      razaoSocial: v.brandName || '',
      cnpj: v.buyerCnpj || '',
      endereco: v.buyerAddress || '',
      emailContato: v.buyerEmail || '',
      telefoneContato: v.buyerPhone || '',
      urlSite: v.portalSubdomain || '',
      mensagem: v.welcomeMessage || '',
      camposAtivos: this.fields.filter(field => this.isFieldActive(field)).map(field => field.key),
    };

    this.dialog.open(PortalCotacaoPreviewComponent, {
      width: 'min(920px, 96vw)',
      maxWidth: '96vw',
      autoFocus: false,
      data,
    });
  }

  public ngOnInit(): void {
    this.carregarEmpresa();
    this.carregarConfiguracao();
  }

  // Razão social / CNPJ / endereço são dados de cadastro da empresa (db_usuario):
  // exibidos somente leitura aqui, não persistem na configuração da cotação.
  private carregarEmpresa(): void {
    const idEmpresa = this.authService.getUserInfos()?.idEmpresa;
    if (!idEmpresa) {
      return;
    }
    this.getEmpresaService.obterPorId(idEmpresa).subscribe({
      next: response => {
        if (response.isError || !response.empresa) {
          return;
        }
        const empresa = response.empresa;
        this.form.patchValue({
          brandName: empresa.nomeEmpresa ?? '',
          buyerCnpj: empresa.cnpj ?? '',
          buyerAddress: this.montarEndereco(empresa),
        });
      },
      error: () => { /* dados da empresa são complementares; falha silenciosa */ },
    });
  }

  private montarEndereco(empresa: EmpresaStruct): string {
    const numero = [empresa.logradouro, empresa.numero].filter(Boolean).join(', ');
    const cidadeUf = [empresa.municipio, empresa.uf].filter(Boolean).join('/');
    const partes = [numero, empresa.complemento, empresa.bairro, cidadeUf, empresa.cep ? `CEP ${empresa.cep}` : null];
    return partes.filter(Boolean).join(' - ');
  }

  public ngOnDestroy(): void {
    this.editorEmail.destroy();
  }

  private carregarConfiguracao(): void {
    this.isLoading = true;
    this.configuracaoCotacaoService.get().subscribe({
      next: response => {
        this.isLoading = false;
        if (response.isError) {
          this.alertService.show('Atenção', response.errorDescription || 'Não foi possível carregar a configuração.', AlertType.warning);
          return;
        }
        this.aplicarConfiguracao(response);
        this.configCarregada = true;
      },
      error: () => {
        this.isLoading = false;
        this.alertService.show('Erro', 'Não foi possível carregar a configuração.', AlertType.error);
      },
    });
  }

  // Popula o formulário a partir da resposta. Formatos e campos vêm como listas
  // (ids / chaves habilitadas) e são convertidos de volta para os toggles da tela.
  private aplicarConfiguracao(response: GetConfiguracaoCotacaoResponse): void {
    this.logoDataUrl = response.urlLogo ?? null;

    this.form.patchValue({
      brandShort: response.marcaSigla ?? '',
      brandTagline: response.marcaRodape ?? '',
      brandColor: response.corMarca || '#B73232',
      heroBg: response.corHeroInicio || '#1C1B1F',
      heroBgEnd: response.corHeroFim || '#2D2A3A',
      buyerEmail: response.emailContato ?? '',
      buyerPhone: response.telefoneContato ?? '',
      portalSubdomain: response.urlSite ?? '',
      welcomeMessage: response.mensagem ?? '',
      emailSubject: response.emailAssunto ?? '',
      emailBody: response.emailCorpo ?? '',
    });

    // A versão de fábrica é do backend: a tela só a guarda para o "Restaurar texto padrão".
    this.emailAssuntoPadrao = response.emailAssuntoPadrao ?? '';
    this.emailCorpoPadrao = response.emailCorpoPadrao ?? '';

    const formatosSelecionados = new Set(response.formatos ?? []);
    this.formatOptions.forEach(format =>
      this.form.get(format.key)?.setValue(formatosSelecionados.has(format.id)));

    const chavesCampos = new Set((response.campos ?? []).map(c => c.chave));
    this.fields.forEach(field =>
      this.form.get(field.key)?.setValue(field.required || chavesCampos.has(field.key)));

    this.form.markAsPristine();
  }

  public voltar(): void {
    this.router.navigate(['/configuracoes/suprimentos']);
  }

  public salvar(): void {
    if (!this.configCarregada) {
      this.alertService.show('Atenção', 'A configuração atual não pôde ser carregada. Recarregue a página antes de salvar.', AlertType.warning);
      return;
    }

    this.isSaving = true;
    const request = this.montarRequest();
    this.configuracaoCotacaoService.salvar(request).subscribe({
      next: response => {
        this.isSaving = false;
        if (response.isError) {
          this.alertService.show('Atenção', response.errorDescription || 'Não foi possível salvar a configuração.', AlertType.warning);
          return;
        }
        this.form.markAsPristine();
        this.alertService.show('Sucesso', 'Configuração salva com sucesso.', AlertType.success);
      },
      error: () => {
        this.isSaving = false;
        this.alertService.show('Erro', 'Não foi possível salvar a configuração.', AlertType.error);
      },
    });
  }

  // Monta o payload achatando os toggles de formato/campo de volta para listas.
  private montarRequest(): ConfiguracaoCotacaoRequest {
    const v = this.form.getRawValue();
    const request = new ConfiguracaoCotacaoRequest();

    request.marcaSigla = v.brandShort || null;
    request.marcaRodape = v.brandTagline || null;
    request.urlLogo = this.logoDataUrl;
    request.corMarca = v.brandColor || '#B73232';
    request.corHeroInicio = v.heroBg || '#1C1B1F';
    request.corHeroFim = v.heroBgEnd || '#2D2A3A';
    request.emailContato = v.buyerEmail || null;
    request.telefoneContato = v.buyerPhone || null;
    request.urlSite = v.portalSubdomain || null;
    request.mensagem = v.welcomeMessage || null;
    request.emailAssunto = v.emailSubject || null;
    request.emailCorpo = v.emailBody || null;

    request.formatos = this.formatOptions
      .filter(format => !!this.form.get(format.key)?.value)
      .map(format => format.id);

    request.campos = this.fields
      .filter(field => this.isFieldActive(field))
      .map(field => ({
        chave: field.key,
        idTipoCampo: field.idTipoCampo,
        nome: field.name,
        isObrigatorio: field.required,
      }));

    return request;
  }

  public toggleVariaveisEmail(): void {
    this.showVariaveisEmail = !this.showVariaveisEmail;
  }

  // Repõe a versão de fábrica do assunto e da mensagem — os dois compõem o template de e-mail.
  // Sem a versão de fábrica em mãos, não faz nada: apagar o texto do usuário seria pior.
  public restaurarTemplateEmail(): void {
    if (!this.emailAssuntoPadrao && !this.emailCorpoPadrao) {
      this.alertService.show('Atenção', 'Não foi possível obter o texto padrão. Recarregue a página e tente novamente.', AlertType.warning);
      return;
    }

    this.form.get('emailSubject')?.setValue(this.emailAssuntoPadrao);
    this.form.get('emailBody')?.setValue(this.emailCorpoPadrao);
    this.form.get('emailSubject')?.markAsDirty();
    this.form.get('emailBody')?.markAsDirty();
  }

  public onColorChange(event: Event, controlName: string): void {
    const input = event.target as HTMLInputElement;
    const color = input.value;
    if (color && color.startsWith('#') && (color.length === 4 || color.length === 7)) {
      this.form.get(controlName)?.setValue(color, { emitEvent: false });
      this.form.markAsDirty();
    }
  }

  public applyPreset(preset: PresetColor): void {
    this.form.patchValue({
      brandColor: preset.color,
      heroBg: preset.heroBg,
      heroBgEnd: preset.heroBgEnd,
    });
    this.form.markAsDirty();
  }

  public onLogoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      this.logoDataUrl = String(reader.result);
      this.form.markAsDirty();
    };
    reader.readAsDataURL(file);
  }

  public removeLogo(): void {
    this.logoDataUrl = null;
    this.form.markAsDirty();
  }

  public insertVariable(variable: string): void {
    this.editorEmail.commands.insertText(variable).focus().exec();
    this.form.get('emailBody')?.markAsDirty();
  }

  public isFormatSelected(formatKey: string): boolean {
    return !!this.form.get(formatKey)?.value;
  }

  public toggleFormat(formatKey: string): void {
    const control = this.form.get(formatKey);
    if (!control) {
      return;
    }

    control.setValue(!control.value);
    control.markAsDirty();
  }

  public isFieldActive(field: FieldConfig): boolean {
    return field.required || !!this.form.get(field.key)?.value;
  }

  public get requiredFields(): FieldConfig[] {
    return this.fields.filter(field => field.required);
  }

  public get optionalFields(): FieldConfig[] {
    return this.fields.filter(field => !field.required);
  }

  public get hasSelectedFormat(): boolean {
    return this.formatOptions.some(format => !!this.form.get(format.key)?.value);
  }

  private createForm(): FormGroup {
    return this.formBuilder.group({
      brandShort: ['ATS'],
      brandTagline: ['Portal do Fornecedor'],
      brandColor: ['#B73232'],
      heroBg: ['#1C1B1F'],
      heroBgEnd: ['#2D2A3A'],
      // Somente leitura: preenchidos a partir do cadastro da empresa (db_usuario).
      brandName: [{ value: '', disabled: true }],
      buyerCnpj: [{ value: '', disabled: true }],
      buyerAddress: [{ value: '', disabled: true }],
      buyerEmail: [''],
      buyerPhone: [''],
      portalSubdomain: [''],
      welcomeMessage: ['Preencha sua proposta com atenção. A equipe de compras analisará preço, prazo e disponibilidade.'],
      formatoLinkPortal: [true],
      formatoPlanilhaExcel: [false],
      formatoPdfPreencher: [false],
      campoPrecoUnitario: [true],
      campoIpi: [true],
      campoSt: [true],
      campoPrazoEntrega: [true],
      campoDisponibilidade: [true],
      campoFrete: [true],
      campoCondicaoPagamento: [true],
      campoValidadeProposta: [true],
      // Assunto e corpo chegam do backend: é lá que o texto da empresa fica gravado.
      emailSubject: [''],
      emailBody: [''],
    });
  }
}
